import { inject } from '@angular/core';
import { ActivityEntityName, EntityViewDisplay, FeatureFlags, FinanceSecurityRight, Payment, SecurityModuleTag, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import type { EntityManifestCard } from '@workbuddy/services/entities/entity.manifest';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { entityRecordUrl } from '@workbuddy/ui/v2/blocks/entity.record';
import { paymentCardRow } from './presentation/payment.card.row';
import { paymentAccountingTag, paymentStatusTag, paymentTypeTag } from './presentation/payment.status.colour';

// The Bill/Invoice Payments section lists rows read in full by the section's own query, so the card
// never re-fetches one; `snapshot` keeps a document with many payments to a single request.
const card: EntityManifestCard<Payment> = {
  component: () => import('./presentation/card.payment').then(module => module.CardPaymentComponent),
  resolution: 'snapshot',
  summarize: payment => {

    const row = paymentCardRow(payment);
    return { title: row.meta, line1: row.receiver ? `${row.receiver.label} ${row.receiver.name}` : undefined };
  },
};

// Registered when this manifest module is loaded at bootstrap.
// Consumers resolve manifests via getManifest/EntityManifestService only.
//
// Read-only board. Payments are written by the invoice/bill payment pipelines, Stripe and accounting
// sync — no create, no edit, no delete — so the manifest declares `preview` and nothing else.
export const paymentManifest = registerManifest<Payment>({
  entity: ActivityEntityName.Payment,
  slug: 'payments',
  featureFlag: FeatureFlags.FinanceDocsPaymentV2,
  menu: {
    group: 'finance',
    label: 'Payments',
    icon: 'fa-credit-card',
    order: 50,
    badge: SecurityModuleTag.Beta,
    authorize: () => inject(RightsService).hasRights(FinanceSecurityRight.CanAccessPayments),
  },

  routes: {
    root: '/entity/payments',
    // Generic record page: legacy payments have a board but no record page.
    record: id => entityRecordUrl('payments', 'preview', id),
  },

  board: {
    // A payment has no lifecycle to move through, so Kanban would only ever be one column.
    supportedDisplays: [EntityViewDisplay.Table],
    panel: { kind: 'preview' },
    defaults: {
      // Not just chrome: `readonly` clears the board's mutation entry points wholesale — add
      // commands, row commands, multi-select and bulk — so nothing can be wired into them later
      // without deliberately removing this. Row focus and the preview panel are unaffected.
      readonly: true,
      empty: { title: 'No payments found.' },
      header: {
        title: 'Payments',
        showSearch: true,
        showPaginator: true,
        showViewOptions: true,
        hideAdd: true,
      },
    },
  },

  presentation: {
    card,
    // Direction, processing result and accounting outcome are three independent facts; the amount
    // itself is a column, so no pill repeats it.
    tags: {
      type: paymentTypeTag,
      status: paymentStatusTag,
      integration: paymentAccountingTag,
    },
  },

  surfaces: {
    [SurfaceType.Detail]: () => import('./surface/detail/payment.detail.surface').then(module => module.paymentDetailSurface),
  },
  operations: {
    preview: {
      surface: SurfaceType.Detail,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessPayments),
    },
  },
});
